import styles from "../../pages/Home/Home.module.css";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Pagination } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import useAppState from "../../hooks/useAppState";
import axios from "axios";
import Loading from "../../components/Loader/Loader";
import { AppConfig } from "../../utils/config";
import { cleanHtmlString } from "../../utils/constants";
import { Product, scrollToTop } from "../../pages/Home/Home";
import { useScrollPosition } from "../../hooks/useScrollPosition";
import { Helmet } from "react-helmet";
import Search from "../Search/Search";

type ItemsProps = {
  isHome: boolean;
  category?: number;
};

export default function Items({ isHome, category }: ItemsProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hidePaginator, setHidePaginator] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(!!searchParams.get("search"));

  const {
    shoppingCart,
    setShoppingCart,
    selectedPage,
    setSelectedPage,
    selectedCategoryName,
  } = useAppState();
  const { saveScrollPosition, restoreScrollPosition, clearScrollPosition } =
    useScrollPosition("items-scroll-position");

  const navigate = useNavigate();
  const hasSearchQuery = !!searchParams.get("search");

  const getProducts = useCallback(async () => {
    const productUrl = isHome
      ? `${AppConfig.apiUrl}/products`
      : `${AppConfig.apiUrl}/products?page=${selectedPage}${
          category ? `&category=${category}` : ""
        }`;

    setLoading(true);
    axios
      .get(productUrl)
      .then((res) => {
        setProducts(res.data.Products);
        setTotalCount(res.data.TotalCount);
        setHidePaginator(res.data.HidePaginator);
        setTimeout(() => {
          restoreScrollPosition();
        }, 250);
      })
      .catch((err) => console.warn(err))
      .finally(() => setLoading(false));
  }, [category, isHome, selectedPage, restoreScrollPosition]);

  useEffect(() => {
    // Skip loading default products if a search query is in the URL;
    // the Search component will handle fetching results instead.
    if (!hasSearchQuery) {
      getProducts();
    }
  }, [category, getProducts, hasSearchQuery]);

  function addToBasket(data: Product) {
    setShoppingCart([...shoppingCart, data]);
  }

  const setSearchProducts = useCallback(
    (products: number[] | null) => {
      if (!products) {
        getProducts();
        return;
      }

      if (products.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }

      axios
        .post(`${AppConfig.apiUrl}/productsByIds`, {
          ids: products,
        })
        .then((res) => setProducts(res.data))
        .catch((err) => console.warn(err))
        .finally(() => setLoading(false));
    },
    [getProducts]
  );

  function removeFromBasket(data: Product) {
    const newCart = shoppingCart.filter((a) => a.ID !== data.ID);
    setShoppingCart(newCart);
  }

  function itemInCart(id: number) {
    return shoppingCart.some((a) => a.ID === id) || false;
  }

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    clearScrollPosition();
    setSelectedPage(value);
    scrollToTop();
  };

  return (
    <div>
      <Helmet>
        <title>
          {isHome
            ? "Martin Townsend - Postal History & Stamps"
            : `${
                selectedCategoryName || "Category"
              } | Martin Townsend Postal History`}
        </title>
        <meta
          name="description"
          content={
            isHome
              ? "Established over 50 years and a full PTS member. We stock significant Postal History and Lettels from 1400 to 1930 and are also major Valuers and Buyers."
              : `Browse ${
                  selectedCategoryName || "category"
                } items from Martin Townsend's postal history collection. Authentic stamps and postal materials with detailed descriptions and competitive prices.`
          }
        />
      </Helmet>
      <div className={styles.searchBar}>
        <Search setSearchProducts={setSearchProducts} category={category} />
      </div>

      {loading ? (
        <div className={styles.inlineLoader}>
          <Loading />
        </div>
      ) : (
        <>
          {!hidePaginator && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                marginTop: "1rem",
              }}
            >
              <Pagination
                count={Math.ceil(totalCount / 60)}
                variant="outlined"
                shape="rounded"
                page={selectedPage}
                onChange={handlePageChange}
              />
            </div>
          )}

          {products && products.length > 0 ? (
            <div className={styles.itemContainer} onClick={saveScrollPosition}>
              {products.map((product) => {
                return (
                  <div key={product.ID} className={styles.item}>
                    <div className={styles.itemDescription}>
                      <Link
                        style={{ textDecoration: "none" }}
                        to={`/item/${product.ID}`}
                        tabIndex={-1}
                      >
                        <div
                          style={{
                            height: "160px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={`${AppConfig.smImgUrl}/${product.ID}.jpg`}
                            alt={`${product.ID} item`}
                          />
                        </div>
                      </Link>
                      <br />
                      <Link
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                          fontFamily: "Book Antiqua",
                        }}
                        to={`/item/${product.ID}`}
                        tabIndex={-1}
                        dangerouslySetInnerHTML={{
                          __html: cleanHtmlString(product.Title),
                        }}
                      />
                    </div>
                    <div className={styles.itemBuyZone}>
                      <div
                        style={{ paddingBottom: "0.5rem", fontWeight: "bold" }}
                      >
                        £{product.Price}
                      </div>
                      {!itemInCart(product.ID) ? (
                        <Button
                          onClick={() =>
                            addToBasket({
                              ...product,
                            })
                          }
                          size="small"
                          variant="contained"
                          disableElevation
                        >
                          Add to basket
                        </Button>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-evenly",
                            gap: "0.5rem",
                          }}
                        >
                          <Button
                            style={{ width: "50%" }}
                            onClick={() =>
                              removeFromBasket({
                                ...product,
                              })
                            }
                            size="small"
                            variant="contained"
                            disableElevation
                          >
                            Remove
                          </Button>
                          <Button
                            style={{ width: "50%" }}
                            size="small"
                            variant="contained"
                            disableElevation
                            onClick={() => navigate("/review")}
                          >
                            Basket
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.noProductsFound}>
              <p>No products found. Please adjust your search criteria.</p>
            </div>
          )}
          {!hidePaginator && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                marginBottom: "1rem",
              }}
            >
              <Pagination
                count={Math.ceil(totalCount / 60)}
                variant="outlined"
                shape="rounded"
                page={selectedPage}
                onChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
